import { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Button } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { VsTransferEvent } from '@vs-online-only-app/state';
import { OutgoingTransferCard } from './OutgoingTransferCard';

type WrapperProps = {
  $hasMultipleTransfers: boolean;
};

const placementStyles = css<WrapperProps>`
  bottom: ${({ $hasMultipleTransfers, theme }) => {
    if (!$hasMultipleTransfers) {
      return theme.spacing(2);
    }
    return theme.spacing(7);
  }};
`;

const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  right: ${({ theme }) => theme.spacing(1)};
  z-index: 1;

  ${placementStyles}
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  height: 100%;
  overflow: auto;
  max-height: calc(100vh - 154px);
  padding: ${({ theme }) => theme.spacing(2, 0)};
`;

const ScrollMask = styled.div<{
  $position: 'top' | 'bottom';
  $visible: boolean;
}>`
  position: absolute;
  right: 0;
  width: 100%;
  height: ${({ theme }) => theme.spacing(7)};
  z-index: 2;
  pointer-events: none;
  background: ${({ $position }) =>
    $position === 'top'
      ? 'linear-gradient(to bottom, #F3F3F7 0%, rgba(243,243,247,0) 100%)'
      : 'linear-gradient(to bottom, rgba(243, 243, 247, 0) 0%, #F3F3F7 100%)'};
  ${({ $position }) => $position === 'top' && 'top: 0;'}
  ${({ $position }) => $position === 'bottom' && 'bottom: 0;'}
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s;
`;

const ShowLessButton = styled(Button)`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing(2)};
  right: ${({ theme }) => theme.spacing(1)};
  z-index: 2;
`;

type Props = {
  transferRequests: VsTransferEvent[];
  onClick: () => void;
  onClose: (request: VsTransferEvent) => void;
  onCancel: (request: VsTransferEvent) => void;
};

export const ExpandedTransferCards: FC<Props> = ({
  transferRequests,
  onClick,
  onClose,
  onCancel,
}) => {
  const [scrolledFromTop, setScrolledFromTop] = useState<boolean>(false);
  const [scrolledFromBottom, setScrolledFromBottom] = useState<boolean>(false);
  const hasMultipleTransfers = transferRequests.length > 1;
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!wrapperRef.current) {
        return;
      }
      const scrollTop = wrapperRef.current.scrollTop;
      setScrolledFromTop(Boolean(scrollTop));
      const scrolledFromBottom =
        wrapperRef.current.scrollHeight - wrapperRef.current.scrollTop >
        wrapperRef.current.clientHeight;
      setScrolledFromBottom(scrolledFromBottom);
    };
    wrapperRef.current?.addEventListener('scroll', handleScroll);
    return () =>
      wrapperRef.current?.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Wrapper $hasMultipleTransfers={hasMultipleTransfers}>
      <InnerWrapper ref={wrapperRef}>
        {Object.values(transferRequests).map((request) => (
          <OutgoingTransferCard
            key={request.id}
            transferRequest={request}
            onClose={() => onClose(request)}
            onCancel={() => onCancel(request)}
            slideIn
          />
        ))}
      </InnerWrapper>
      <ScrollMask $position="top" $visible={scrolledFromTop} />
      <ScrollMask $position="bottom" $visible={scrolledFromBottom} />
      {hasMultipleTransfers && (
        <ShowLessButton
          size="small"
          endIcon={<ExpandMore />}
          onClick={onClick}
          variant="contained"
        >
          Show less
        </ShowLessButton>
      )}
    </Wrapper>
  );
};
